(ns io.daskul.webapp.format
  "Global formatting utilities.

   Money presentation is configured once from the /_config response as
   {:symbol :locale :decimals}. Grouping and decimal separators are derived
   from the locale rather than configured, so the number input and the
   read-only column cannot disagree about what an amount looks like."
  (:require
   [clojure.string :as str]))

(defonce money-config (atom {:symbol "₱" :locale "en" :decimals 2}))

(defn set-money-config!
  "Apply the money settings from the /_config response. Absent keys keep their
   current value, so the app renders money from boot."
  [cfg]
  (swap! money-config merge
         (into {} (remove (comp nil? val)) (select-keys cfg [:symbol :locale :decimals]))))

(defn- locale-separators [locale]
  (reduce (fn [acc part]
            (case (.-type part)
              "group" (assoc acc :thousandSeparator (.-value part))
              "decimal" (assoc acc :decimalSeparator (.-value part))
              acc))
          {}
          (.formatToParts (js/Intl.NumberFormat. locale) 1234.5)))

(def ^:private separators (memoize locale-separators))

(defn number-input-props
  "Separator props for any numeric input, derived from the configured locale.
   Mantine needs literal strings, which is why they are read out here."
  []
  (separators (:locale @money-config)))

(defn currency-input-props
  "Number-input props for a money field: the separators every numeric gets,
   plus the currency symbol and a fixed number of decimals."
  []
  (let [{:keys [symbol decimals]} @money-config]
    (assoc (number-input-props)
           :prefix symbol
           :decimalScale decimals
           :fixedDecimalScale true)))

(defn format-currency
  "Format a numeric amount as currency, e.g. ₱1,234.56.
   :decimals overrides the configured precision (dashboard cards ask for 0)."
  ([amount] (format-currency amount nil))
  ([amount opts]
   (when amount
     (let [cfg @money-config
           d (or (:decimals opts) (:decimals cfg))
           formatted (.format (js/Intl.NumberFormat. (:locale cfg)
                                                     #js {:minimumFractionDigits d
                                                          :maximumFractionDigits d})
                              (js/Number. amount))]
       (str (:symbol cfg) formatted)))))

(defn parse-number
  "Turn what a number input reports back into a number.

   Accepts a grouped amount (\"3,000.00\") because the widget lets one be typed
   or pasted — js/parseFloat alone would read that as 3. Blank is nil, never 0."
  ([v] (parse-number v (number-input-props)))
  ([v {:keys [thousandSeparator decimalSeparator]}]
   (cond
     (number? v) v
     (nil? v) nil
     :else
     (let [s (cond-> (str/trim (str v))
               (seq thousandSeparator) (str/replace thousandSeparator "")
               (seq decimalSeparator) (str/replace decimalSeparator "."))]
       (when-not (str/blank? s)
         (let [n (js/parseFloat s)]
           (when-not (js/isNaN n) n)))))))
